import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';

const COMPLETED_CLEANUP_STATUSES = ['completed', 'cancelled'] as const;
const FAILED_CLEANUP_STATUSES = ['failed', 'partial_failed'] as const;

export type JobsCleanupEligibility = {
  completedCount: number;
  failedCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
};

function readEligibleCount(result: {
  eligibleCount?: number;
  matched?: number;
}): number {
  return result.eligibleCount ?? result.matched ?? 0;
}

export function useJobsCleanupEligibility(
  enabled: boolean,
  refreshSignal: unknown = null,
): JobsCleanupEligibility {
  const [completedCount, setCompletedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [loading, setLoading] = useState(enabled);

  const loadedRef = useRef(false);

  const refresh = useCallback(async (options?: { background?: boolean }) => {
    if (!enabled) {
      setCompletedCount(0);
      setFailedCount(0);
      setLoading(false);
      loadedRef.current = false;
      return;
    }

    const background = options?.background ?? loadedRef.current;
    if (!background) {
      setLoading(true);
    }
    try {
      const [completedPreview, failedPreview] = await Promise.all([
        api.jobs.cleanupPreview({ statuses: [...COMPLETED_CLEANUP_STATUSES] }),
        api.jobs.cleanupPreview({ statuses: [...FAILED_CLEANUP_STATUSES] }),
      ]);
      setCompletedCount(readEligibleCount(completedPreview));
      setFailedCount(readEligibleCount(failedPreview));
      loadedRef.current = true;
    } catch {
      setCompletedCount(0);
      setFailedCount(0);
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    void refresh({ background: loadedRef.current });
  }, [refresh, refreshSignal]);

  return { completedCount, failedCount, loading, refresh };
}
